import React, { useMemo, useCallback, forwardRef } from "react";

import { Brush as VisxBrush } from "@visx/brush";
import { Group } from "@visx/group";

import { compose, useXScale, useYScale } from "../../helpers";

import { Bars } from "../../components/Bars";

import type { BrushProps } from "./types";
import { AnyScaleBand } from "@visx/shape/lib/types";

const handleSize = 8;

const Brush = (
  {
    top,
    left,
    height,
    sets,
    onChange,
    leftAccessor,
    rightAccessor,
    xMax,
    bottomAccessor,
    bottomAxis,
    thresholds,
    from,
    to,
    interval,
  }: BrushProps,
  brushRef: any
) => {
  const { scale, getPoint } = useXScale({
    xMax,
    data: sets,
    comparator: bottomAxis.compare,
    bottomAccessor,
    variant: "brush",
    from,
    to,
    interval,
  });

  const yMax = useMemo(() => Math.max(height, 0), [height]);

  const leftScale = useYScale({
    yMax,
    data: sets.reduce(
      (total: number[], { data, axis }) =>
        axis === "left"
          ? [...total, ...data.map((point: any) => leftAccessor(point))]
          : total,
      []
    ),
    maxThreshold: thresholds.reduce(
      (currentMax, { axis, value }) =>
        axis === "left" && value > currentMax ? value : currentMax,
      0
    ),
    hasBars: true,
  });

  const rightScale = useYScale({
    yMax,
    data: sets.reduce(
      (total: number[], { data, axis }) =>
        axis === "right"
          ? [...total, ...data.map((point: any) => rightAccessor?.(point))]
          : total,
      []
    ),
    maxThreshold: thresholds.reduce(
      (currentMax, { axis, value }) =>
        axis === "right" && value > currentMax ? value : currentMax,
      0
    ),
    hasBars: true,
  });

  const leftPoint = useCallback(compose(leftScale, leftAccessor), [
    leftScale,
    leftAccessor,
  ]);

  const rightPoint = useCallback(
    rightAccessor ? compose(rightScale, rightAccessor) : () => null,
    [rightScale, rightAccessor]
  );

  const onClearSelection = () => {
    if (brushRef?.current) {
      onChange();
      brushRef.current.reset();
    }
  };

  return (
    <Group
      top={top}
      left={left}
    >
      <rect
        width={xMax}
        height={yMax}
        rx={4}
        fill={"#F2F5F6"}
      ></rect>

      <Bars
        bottomScale={scale as AnyScaleBand}
        yMax={yMax}
        data={sets.filter(({ variant }) => variant === "bar")}
        colour="#A0ACB2"
        getLeftPoint={leftPoint}
        getRightPoint={rightPoint}
        getBottomPoint={getPoint}
        leftScale={leftScale}
        rightScale={rightScale}
      />

      <VisxBrush
        xScale={scale}
        yScale={leftScale}
        width={xMax}
        height={yMax}
        handleSize={handleSize}
        innerRef={brushRef}
        resizeTriggerAreas={["left", "right"]}
        brushDirection="horizontal"
        onChange={onChange}
        selectedBoxStyle={{
          stroke: "white",
          fillOpacity: 0.2,
          strokeWidth: 1,
        }}
        onClick={onClearSelection}
        useWindowMoveEvents
      />
    </Group>
  );
};

export default forwardRef(Brush);
